import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { FloatingCard, useDisclosure } from '@tyro/core';
import { StarSparklesIcon } from '@tyro/icons';
import { AnimatePresence, m } from 'motion/react';
import { useEffect, useState } from 'react';
import { useTyroneConversation } from '../../../../api/tyrone/conversation';
import { IconButtonAnimate } from '../../../icon-button-animate';
import { ConversationList } from './conversation-list';
import { ConversationView } from './conversation-view';
import { PopoverHeader } from './popover-header';
import type { TyroneMessageThread, TyroneView } from './types';

const slideVariants = {
  enterFromRight: {
    x: '100%',
    opacity: 0,
  },
  enterFromLeft: {
    x: '-100%',
    opacity: 0,
  },
  center: {
    x: 0,
    opacity: 1,
  },
  exitToLeft: {
    x: '-100%',
    opacity: 0,
  },
  exitToRight: {
    x: '100%',
    opacity: 0,
  },
};

export function TyronePopover() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentView, setCurrentView] = useState<TyroneView>('list');
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [conversationTitle, setConversationTitle] = useState<string | null>(
    null,
  );
  const [messageThread, setMessageThread] = useState<TyroneMessageThread>({
    conversationId: undefined,
    messages: [],
  });

  const {
    data: conversationHistory,
    isPending: isConversationPending,
    refetch: refetchConversation,
  } = useTyroneConversation(selectedConversationId);

  useEffect(() => {
    if (selectedConversationId && conversationHistory?.messages) {
      setMessageThread({
        conversationId: selectedConversationId,
        messages: conversationHistory.messages.flatMap((msg) => [
          { id: `${msg.id}-prompt`, by: 'user' as const, text: msg.prompt },
          { id: `${msg.id}-response`, by: 'ai' as const, text: msg.response },
        ]),
      });
    }
  }, [selectedConversationId, conversationHistory]);

  const handleSelectConversation = (
    conversationId: string,
    initialMessage: string,
  ) => {
    setSelectedConversationId(conversationId);
    setConversationTitle(initialMessage);
    setMessageThread({
      conversationId,
      messages: [],
    });
    setCurrentView('conversation');
  };

  const handleNewConversation = () => {
    setSelectedConversationId(null);
    setConversationTitle(null);
    setMessageThread({
      conversationId: undefined,
      messages: [],
    });
    setCurrentView('conversation');
  };

  const handleBackToList = () => {
    setCurrentView('list');
  };

  const handleClose = () => {
    onClose();
    setCurrentView('list');
    setSelectedConversationId(null);
    setConversationTitle(null);
    setMessageThread({
      conversationId: undefined,
      messages: [],
    });
  };

  return (
    <>
      <Tooltip title="Tyrone">
        <IconButtonAnimate
          color={isOpen ? 'primary' : 'default'}
          onClick={onOpen}
          sx={{ width: 40, height: 40 }}
          aria-label="Tyrone"
        >
          <StarSparklesIcon />
        </IconButtonAnimate>
      </Tooltip>
      <FloatingCard
        open={isOpen}
        onClose={handleClose}
        initialHeight={window.innerHeight - 16}
        initialPosition={{
          bottom: 8,
          right: 8,
        }}
        anchor="right"
        CardProps={{
          sx: {
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <PopoverHeader
          onClose={handleClose}
          showBackButton={currentView === 'conversation'}
          onBack={handleBackToList}
          onNewConversation={
            currentView === 'list' ? undefined : handleNewConversation
          }
          conversationTitle={conversationTitle}
          conversationId={messageThread.conversationId}
        />

        <Box
          sx={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          <AnimatePresence initial={false} mode="popLayout">
            {currentView === 'list' ? (
              <Box
                key="list"
                component={m.div}
                variants={slideVariants}
                initial="enterFromLeft"
                animate="center"
                exit="exitToLeft"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <ConversationList
                  onSelectConversation={handleSelectConversation}
                  onNewConversation={handleNewConversation}
                />
              </Box>
            ) : (
              <Box
                key="conversation"
                component={m.div}
                variants={slideVariants}
                initial="enterFromRight"
                animate="center"
                exit="exitToRight"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <ConversationView
                  messageThread={messageThread}
                  setMessageThread={setMessageThread}
                  onTitleChange={setConversationTitle}
                  isHistoryPending={isConversationPending}
                  refetchHistory={refetchConversation}
                />
              </Box>
            )}
          </AnimatePresence>
        </Box>
      </FloatingCard>
    </>
  );
}
